import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Trophy, Medal, Crown, Coins, Path, Bicycle,
    Lightning, Leaf, Gauge, Timer
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const ICON_MAP = {
    path: Path, leaf: Leaf, lightning: Lightning, bicycle: Bicycle,
    coins: Coins, trophy: Trophy, gauge: Gauge, timer: Timer,
};

export default function Leaderboard() {
    const { user } = useAuth();
    const [boardTypes, setBoardTypes] = useState([]);
    const [activeBoard, setActiveBoard] = useState(null);
    const [leaders, setLeaders] = useState([]);
    const [boardInfo, setBoardInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingBoard, setLoadingBoard] = useState(false);

    useEffect(() => {
        fetch(`${API}/leaderboards/types`)
            .then(r => r.json())
            .then(types => {
                setBoardTypes(types);
                if (types.length > 0) {
                    setActiveBoard(types[0].board_id);
                }
            })
            .catch(e => console.error('Failed to load leaderboard types:', e))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!activeBoard) return;
        setLoadingBoard(true);
        fetch(`${API}/leaderboards/${activeBoard}`)
            .then(r => r.json())
            .then(data => {
                setLeaders(data.leaders || []);
                setBoardInfo(data.board || null);
            })
            .catch(e => console.error('Failed to load leaderboard:', e))
            .finally(() => setLoadingBoard(false));
    }, [activeBoard]);

    const getRankIcon = (rank) => {
        if (rank === 1) return <Crown size={22} className="text-boltz-gold" weight="fill" />;
        if (rank === 2) return <Medal size={22} className="text-white/60" weight="fill" />;
        if (rank === 3) return <Medal size={22} className="text-electric-blue" weight="fill" />;
        return <span className="text-sm font-bold text-muted-foreground font-mono">#{rank}</span>;
    };

    const getName = (l) => {
        if (l.username) return l.username;
        if (l.first_name) return `${l.first_name} ${l.last_name?.charAt(0) || ''}`.trim();
        return 'Anonymous Rider';
    };

    const getInitials = (l) => {
        if (l.first_name) return l.first_name.charAt(0) + (l.last_name?.charAt(0) || '');
        if (l.username) return l.username.charAt(0).toUpperCase();
        return 'U';
    };

    const formatStat = (value, unit) => {
        if (value === undefined || value === null) return '0';
        const num = typeof value === 'number' ? value : parseFloat(value) || 0;
        if (unit === 'km' || unit === 'kg CO2') return num.toFixed(1);
        if (unit === 'km/h') return num.toFixed(1);
        if (unit === 'Boltz') return Math.round(num).toLocaleString();
        return Math.round(num).toLocaleString();
    };

    const BoardIcon = boardInfo?.icon ? (ICON_MAP[boardInfo.icon] || Trophy) : Trophy;

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-primary text-glow">LEADERBOARDS</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display" data-testid="leaderboard-title">Rankings</h1>
                    <p className="text-muted-foreground text-sm mt-1">Compete across multiple categories</p>
                </motion.div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
                ) : (
                    <>
                        {/* Board selector */}
                        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide" data-testid="board-selector">
                            {boardTypes.map(bt => {
                                const Icon = ICON_MAP[bt.icon] || Trophy;
                                const isActive = activeBoard === bt.board_id;
                                return (
                                    <button key={bt.board_id}
                                        onClick={() => setActiveBoard(bt.board_id)}
                                        data-testid={`board-${bt.board_id}`}
                                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-display whitespace-nowrap transition-all shrink-0 ${
                                            isActive
                                                ? 'bg-primary/15 text-primary border border-primary/30'
                                                : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/15'
                                        }`}>
                                        <Icon size={14} />
                                        {bt.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Board header */}
                        {boardInfo && (
                            <motion.div key={boardInfo.board_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                className="flex items-center gap-3 mb-5 p-3 rounded-xl bg-white/5 border border-white/5">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                    <BoardIcon size={20} className="text-primary" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-sm font-display">{boardInfo.name}</p>
                                    <p className="text-[10px] text-muted-foreground">{boardInfo.description}</p>
                                </div>
                                <Badge className="bg-primary/10 text-primary text-[10px] border-primary/20">{boardInfo.unit}</Badge>
                            </motion.div>
                        )}

                        {/* Podium */}
                        {!loadingBoard && leaders.length >= 3 && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                className="grid grid-cols-3 gap-3 mb-6">
                                {/* 2nd */}
                                <div className="flex flex-col items-center pt-6">
                                    <Avatar className="w-14 h-14 border-2 border-white/20">
                                        <AvatarImage src={leaders[1]?.picture} />
                                        <AvatarFallback className="bg-muted text-foreground text-sm">{getInitials(leaders[1])}</AvatarFallback>
                                    </Avatar>
                                    <div className="w-6 h-6 -mt-2 bg-white/20 rounded-full flex items-center justify-center text-xs font-bold font-display z-10">2</div>
                                    <p className="mt-1 text-xs truncate max-w-[80px]">{getName(leaders[1])}</p>
                                    <p className="font-mono text-xs text-primary font-bold">{formatStat(leaders[1]?.stat_value, boardInfo?.unit)}</p>
                                </div>
                                {/* 1st */}
                                <div className="flex flex-col items-center">
                                    <Crown size={24} className="text-boltz-gold mb-1" weight="fill" />
                                    <Avatar className="w-16 h-16 border-2 border-boltz-gold shadow-[0_0_12px_rgba(255,215,0,0.3)]">
                                        <AvatarImage src={leaders[0]?.picture} />
                                        <AvatarFallback className="bg-boltz-gold/20 text-boltz-gold font-bold">{getInitials(leaders[0])}</AvatarFallback>
                                    </Avatar>
                                    <div className="w-6 h-6 -mt-2 bg-boltz-gold rounded-full flex items-center justify-center text-xs font-bold text-black font-display z-10">1</div>
                                    <p className="mt-1 text-xs font-bold truncate max-w-[80px]">{getName(leaders[0])}</p>
                                    <p className="font-mono text-sm text-boltz-gold font-bold">{formatStat(leaders[0]?.stat_value, boardInfo?.unit)}</p>
                                </div>
                                {/* 3rd */}
                                <div className="flex flex-col items-center pt-8">
                                    <Avatar className="w-12 h-12 border-2 border-electric-blue/40">
                                        <AvatarImage src={leaders[2]?.picture} />
                                        <AvatarFallback className="bg-electric-blue/10 text-electric-blue text-sm">{getInitials(leaders[2])}</AvatarFallback>
                                    </Avatar>
                                    <div className="w-6 h-6 -mt-2 bg-electric-blue/50 rounded-full flex items-center justify-center text-xs font-bold font-display z-10">3</div>
                                    <p className="mt-1 text-xs truncate max-w-[80px]">{getName(leaders[2])}</p>
                                    <p className="font-mono text-xs text-electric-blue font-bold">{formatStat(leaders[2]?.stat_value, boardInfo?.unit)}</p>
                                </div>
                            </motion.div>
                        )}

                        {/* Full list */}
                        {loadingBoard ? (
                            <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
                        ) : leaders.length === 0 ? (
                            <Card className="glass-card text-center py-12">
                                <Trophy size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground font-display">No riders yet</p>
                                <p className="text-xs text-muted-foreground mt-1">Start riding to appear on the leaderboard</p>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {leaders.map((l, i) => {
                                    const isMe = l.user_id === user?.user_id;
                                    return (
                                        <motion.div key={l.user_id || i} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
                                            <Card className={`glass-card ${isMe ? 'border-primary/40' : ''}`} data-testid={`leader-row-${i}`}>
                                                <CardContent className="p-3 flex items-center gap-3">
                                                    <div className="w-8 flex justify-center shrink-0">{getRankIcon(l.rank || i + 1)}</div>
                                                    <Avatar className="w-9 h-9 shrink-0">
                                                        <AvatarImage src={l.picture} />
                                                        <AvatarFallback className="bg-muted text-xs">{getInitials(l)}</AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">
                                                            {getName(l)}
                                                            {isMe && <span className="text-primary text-xs ml-1">(You)</span>}
                                                        </p>
                                                        <p className="text-[10px] text-muted-foreground">Level {l.level || 1}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <p className="font-mono font-bold text-sm text-primary">{formatStat(l.stat_value, boardInfo?.unit)}</p>
                                                        <p className="text-[10px] text-muted-foreground">{boardInfo?.unit}</p>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
